import React, { createContext, useState } from 'react';
import { SprSimListTabName } from 'types/your-sims';
import { PortInRequestStatus, SimStatus } from 'types/shared';
import { useFilteredSims } from 'lib/hooks/sim/sim.queries';
import { QueryData } from 'lib/api/utils';
import { DebounceInput } from 'react-debounce-input';
import { clsx } from 'clsx';
import { usePDFDownload, useUpdateSimName } from 'lib/hooks/sim/sim.mutations';
import {
  useModalsContext,
  useSimRowContext,
  useYourSimsContext,
} from 'views/YourSiMs/hooks';
import NumberFormat from 'react-number-format';
import moment from 'moment';
import { columns } from '../../config';
import { useGetAllPlans } from 'lib/hooks/plan/plan.queries';
import { useAuthContext } from 'contexts/auth.context';
import { message } from 'antd';
import { imeiSchema } from 'schemas/common.schemas';

type SimRowProps = {
  simRecord: QueryData<typeof useFilteredSims>['records'][number];
};

type SimRowContextType = SimRowProps & {
  isSimSelected: boolean;
  shouldOpenSimDetails: boolean;
  openSimDetails: () => void;
};

export const SimRowContext = createContext<SimRowContextType | null>(null);

function SimRow(props: SimRowProps) {
  const { simRecord } = props;

  const {
    activeTab,
    oldSelectedSims,
    goToSimDetailsView,
    isUserAllowedToAddClient,
  } = useYourSimsContext();

  const isSimSelected = oldSelectedSims.some(
    (markedSim) => markedSim.id === simRecord.id
  );

  const shouldOpenSimDetails =
    !(
      activeTab === SprSimListTabName.AVAILABLE_PSIM ||
      activeTab === SprSimListTabName.AVAILABLE_ESIM
    ) && !isSimSelected;

  const openSimDetails = () => {
    if (!simRecord?.simnumber) return;

    if (shouldOpenSimDetails) goToSimDetailsView(simRecord.simnumber);
  };

  return (
    <SimRowContext.Provider
      value={{
        simRecord,
        isSimSelected,
        openSimDetails,
        shouldOpenSimDetails,
      }}
    >
      <tr className={shouldOpenSimDetails ? 'cursor-pointer' : ''}>
        {columns.map((col) => {
          const shouldRenderCell =
            col.allowedTabs.includes(activeTab) || col.allowedTabs === '*';

          // do not render client column if user is not allowed to add client
          if (col.key === 'client' && !isUserAllowedToAddClient) return null;

          return shouldRenderCell && col.CellComponent ? (
            <col.CellComponent key={col.key} />
          ) : null;
        })}
      </tr>
    </SimRowContext.Provider>
  );
}

export default SimRow;

export function CheckboxCell() {
  const { onSimRowCheck } = useYourSimsContext();
  const { isSimSelected, simRecord } = useSimRowContext();

  const simStatus = simRecord.status;

  const isCheckboxDisabled = simStatus === SimStatus.SWAPPED;

  return (
    <td className="checkbox-wrap">
      {
        <label className="custom-checkbox-only">
          <input
            disabled={isCheckboxDisabled}
            type="checkbox"
            className="custom-checkbox--input"
            name="check"
            checked={isSimSelected}
            onChange={() => onSimRowCheck(simRecord)}
          />
          <span
            className={
              isCheckboxDisabled ? 'checkmark-only-disabled' : 'checkmark-only'
            }
          ></span>
        </label>
      }
    </td>
  );
}

export function SimNameCell() {
  const { simRecord, isSimSelected, openSimDetails } = useSimRowContext();
  const { activeTab } = useYourSimsContext();
  const updateSimNameMutation = useUpdateSimName();

  const isEsim = simRecord.isEsim === 1;
  const simname = simRecord.simname;
  const simnumber = simRecord.simnumber;

  const isSimNameChangeAllowedInCurrentTab =
    activeTab === SprSimListTabName.AVAILABLE_PSIM ||
    activeTab === SprSimListTabName.AVAILABLE_ESIM;

  const handleSimNameChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const simName = e.target.value;

    await updateSimNameMutation.mutateAsync({
      simname: simName,
      ids: [simRecord.id],
    });
  };

  const simNameChangeField = (
    <div className="material-textfield">
      <DebounceInput
        id="simname-input"
        minLength={3}
        debounceTimeout={500}
        className="form-control form-control-sm material-textfield-input material-textfield-input-sm"
        name="simname"
        value={simname ?? ''}
        onChange={handleSimNameChange}
        required
      />

      <label
        className="material-textfield-label material-textfield-label-sm"
        htmlFor="simname-input"
      >
        SIM Name
      </label>
    </div>
  );

  const handleCellClick = () => {
    if (!isSimSelected && !isSimNameChangeAllowedInCurrentTab) openSimDetails();
  };

  return (
    <td onClick={handleCellClick} id={'1'}>
      <div className="d-flex align-items-center">
        {isSimNameChangeAllowedInCurrentTab && isSimSelected
          ? simNameChangeField
          : simname || simnumber || '-'}

        {isEsim && <span className="blue-chip-label ml-2">eSIM</span>}
      </div>
    </td>
  );
}

export function TagsCell() {
  const { simRecord } = useSimRowContext();
  const { openAddTagsModal } = useModalsContext();

  const tags = simRecord.tags?.length > 0 ? simRecord.tags.split(',') : [];
  const simStatus = simRecord.status;

  return (
    <td>
      {tags.map((tag) => (
        <button
          key={tag}
          className="btn btn-tags mr-2"
          style={{ cursor: 'auto', userSelect: 'all' }}
        >
          {tag}
        </button>
      ))}

      {simStatus !== SimStatus.SWAPPED && (
        <button
          onClick={() => openAddTagsModal(simRecord)}
          className="btn text-underline btn-link-orange"
        >
          Add Tag
        </button>
      )}
    </td>
  );
}

export function ClientCell() {
  const { activeTab } = useYourSimsContext();
  const { simRecord } = useSimRowContext();
  const { openClientModal } = useModalsContext();

  const canAddClientInCurrentTab = [
    SprSimListTabName.ACTIVE,
    SprSimListTabName.ACTIVATION_IN_PROGRESS,
    SprSimListTabName.AVAILABLE_PSIM,
    SprSimListTabName.AVAILABLE_ESIM,
  ].includes(activeTab);

  const clientNamePill = (
    <button
      className="btn btn-tags btn-client mr-2"
      style={{ cursor: 'auto', userSelect: 'all' }}
    >
      {simRecord.clientname}
    </button>
  );

  const addClientButton = (
    <button
      onClick={() => openClientModal(simRecord)}
      className="btn text-underline btn-link-orange"
    >
      Add Client
    </button>
  );

  const renderCellContent = () => {
    if (simRecord.clientname) return clientNamePill;
    if (canAddClientInCurrentTab) return addClientButton;
    return '-';
  };

  return <td>{renderCellContent()}</td>;
}

export function ZipCodeCell() {
  const { activeTab, handleZipcodeChange, oldSelectedSims } =
    useYourSimsContext();
  const { simRecord, isSimSelected, openSimDetails } = useSimRowContext();

  const zipcode = simRecord.zipcode;

  const isZipCodeChangeAllowedInCurrentTab =
    activeTab === SprSimListTabName.AVAILABLE_PSIM ||
    activeTab === SprSimListTabName.AVAILABLE_ESIM;

  const isZipCodeIneligible = oldSelectedSims?.find(
    (sim) => sim?.id === simRecord?.id
  )?.isZipCodeIneligible;

  const zipCodeChangeField = (
    <div
      className={clsx('material-textfield', {
        'input-error': isZipCodeIneligible,
      })}
    >
      <DebounceInput
        minLength={3}
        type="number"
        name="zipcode"
        id="zipcode"
        value={zipcode}
        className="form-control form-control-sm material-textfield-input material-textfield-input-sm"
        onChange={(e) => handleZipcodeChange(simRecord.id, e)}
        required
      />
      <label
        className="material-textfield-label material-textfield-label-sm"
        htmlFor="zipcode"
      >
        Zipcode
      </label>
    </div>
  );

  const handleCellClick = () => {
    if (!isSimSelected && !isZipCodeChangeAllowedInCurrentTab) openSimDetails();
  };

  return (
    <td onClick={handleCellClick}>
      {isZipCodeChangeAllowedInCurrentTab && isSimSelected
        ? zipCodeChangeField
        : zipcode || '-'}
    </td>
  );
}

export function IMEICell() {
  const { activeTab, oldSimDataWithIMEICompatibility, handleIMEIChange } =
    useYourSimsContext();
  const { simRecord, isSimSelected } = useSimRowContext();
  const [isImeiValid, setIsImeiValid] = useState(false);

  const IMEI = simRecord.imeinumber;

  const isIMEIChangeAllowedInCurrentTab =
    activeTab === SprSimListTabName.AVAILABLE_ESIM;

  const isIMEICompatible = oldSimDataWithIMEICompatibility?.find(
    (sim) => sim?.id === simRecord?.id
  )?.isImeiEsimCompatible;

  const imeiChangeHandler: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const imei = e.target.value;

    const { success: isValid } = imeiSchema.safeParse(imei);
    setIsImeiValid(isValid);

    handleIMEIChange(simRecord.id, e);
  };

  const imeiChangeField = (
    <div
      className={clsx('material-textfield', {
        'input-error': !isImeiValid || isIMEICompatible === false,
      })}
    >
      <DebounceInput
        type="number"
        name="imeinumber"
        id="imeinumber"
        className="form-control form-control-sm material-textfield-input material-textfield-input-sm"
        required
        value={IMEI ?? ''}
        onChange={imeiChangeHandler}
        autoComplete="off"
      />

      <label
        className="material-textfield-label material-textfield-label-sm"
        htmlFor="imeinumber"
      >
        IMEI
      </label>
    </div>
  );

  return (
    <td>
      {isIMEIChangeAllowedInCurrentTab && isSimSelected
        ? imeiChangeField
        : IMEI || '-'}
    </td>
  );
}

export function UserIdCell() {
  const { simRecord, openSimDetails } = useSimRowContext();

  return <td onClick={openSimDetails}>{simRecord.ultraUserId || '-'}</td>;
}

export function ICCIDCell() {
  const { simRecord, openSimDetails } = useSimRowContext();

  return <td onClick={openSimDetails}>{simRecord.simnumber || '-'}</td>;
}

export function FailedReasonCell() {
  const { simRecord } = useSimRowContext();

  const reason =
    simRecord.status === SimStatus.ACTIVATION_FAILED
      ? 'ACTIVATION_FAILED'
      : simRecord.failedReason;

  return (
    <td>
      <span className="btn btn-link-danger">{reason || ''}</span>
    </td>
  );
}

export function PortInStatusCell() {
  const { simRecord } = useSimRowContext();

  const portInStatus = simRecord.portInRequestStatus;
  const isPortInPending = portInStatus === PortInRequestStatus.PENDING;

  if (!portInStatus) return <td>&nbsp;</td>;

  return (
    <td>
      <span
        className={clsx('btn', {
          'btn-link-danger': !isPortInPending,
          'btn-green': isPortInPending,
        })}
      >
        {`${portInStatus[0].toUpperCase()}${portInStatus.slice(1)}`}
      </span>
    </td>
  );
}

export function MSISDNCell() {
  const { simRecord, openSimDetails } = useSimRowContext();

  const msisdn = simRecord.msisdn;

  return (
    <td onClick={openSimDetails}>
      {msisdn ? (
        <NumberFormat
          value={msisdn}
          format="(###) ###-####"
          displayType="text"
          allowEmptyFormatting
          mask="_"
        />
      ) : (
        '-'
      )}
    </td>
  );
}

export function ActivationDateCell() {
  const { simRecord, openSimDetails } = useSimRowContext();
  const activationDate = simRecord.activationdate;

  const isValidDate = activationDate && moment(activationDate).isValid();

  return (
    <td onClick={openSimDetails}>
      {isValidDate ? moment(activationDate).format('MMMM DD, YYYY') : '-'}
    </td>
  );
}

export function PlanTitleCell() {
  const { simRecord, openSimDetails } = useSimRowContext();
  const { data: allPlansData, isLoading: allPlansLoading } = useGetAllPlans();

  const plan = allPlansData?.plans.find(
    (plan) => plan.offerId === simRecord.planid
  );

  return (
    <td onClick={openSimDetails}>
      {allPlansLoading ? 'Loading...' : plan?.title || '-'}
    </td>
  );
}

export function PlanIDCell() {
  const { simRecord, openSimDetails } = useSimRowContext();

  return <td onClick={openSimDetails}>{simRecord.planid || '-'}</td>;
}

export function DeactivationDateCell() {
  const { simRecord, openSimDetails } = useSimRowContext();
  const deactivationDate = simRecord.datePlanChanged;

  const isValidDate = deactivationDate && moment(deactivationDate).isValid();

  return (
    <td onClick={openSimDetails}>
      {isValidDate ? moment(deactivationDate).format('MMMM DD, YYYY') : '-'}
    </td>
  );
}

export function PausedDateCell() {
  const { simRecord, openSimDetails } = useSimRowContext();
  const pausedDate = simRecord.datePlanChanged;

  const isValidDate = pausedDate && moment(pausedDate).isValid();

  return (
    <td onClick={openSimDetails}>
      {isValidDate ? moment(pausedDate).format('MMMM DD, YYYY') : '-'}
    </td>
  );
}

export function StatusReasonCell() {
  const { simRecord, openSimDetails } = useSimRowContext();

  return <td onClick={openSimDetails}>{simRecord.StatusReason || '-'}</td>;
}

export function EsimPDFDownloadCell() {
  const { simRecord } = useSimRowContext();
  const pdfDownloadMutation = usePDFDownload();
  const messageKey = 'downloadPdf';

  const downloadPdf = async () => {
    message.open({
      content: 'Dowloading PDF...',
      key: messageKey,
      type: 'loading',
      duration: 0,
    });

    const isSuccess = await pdfDownloadMutation.mutateAsync(
      simRecord.simnumber
    );

    if (isSuccess)
      message.open({
        content: 'PDF Downloaded',
        key: messageKey,
        type: 'success',
      });
    else
      message.open({
        content: 'Something went wrong. Please try again.',
        key: messageKey,
        type: 'error',
      });
  };

  const isEsim = simRecord.isEsim === 1;

  return (
    <td>
      {isEsim ? (
        <span className="download-pdf-list" onClick={downloadPdf}>
          Download
        </span>
      ) : (
        '-'
      )}
    </td>
  );
}

export function ActionsCell() {
  const { simRecord } = useSimRowContext();
  const { isAdmin, isImpersonating } = useAuthContext();
  const { activeTab, singleSimAction } = useYourSimsContext();
  const { openDeleteModal } = useModalsContext();

  const portInStatus =
    simRecord.status === SimStatus.ACTIVATION_IN_PROGRESS &&
    simRecord.portInRequestStatus;

  if (portInStatus) return <td>&nbsp;</td>;

  const activateButton = (
    <button
      type="button"
      onClick={() =>
        activeTab === SprSimListTabName.AVAILABLE_ESIM
          ? singleSimAction('activateEsimbyID', simRecord)
          : singleSimAction('activate', simRecord)
      }
      className="btn btn-link-purple"
    >
      Activate
    </button>
  );

  const deleteButton = (
    <button
      type="button"
      onClick={() => openDeleteModal(simRecord)}
      className="btn btn-link-danger"
    >
      Delete
    </button>
  );

  const canDeleteSim = isAdmin() || isImpersonating();
  const canActivateSim =
    activeTab === SprSimListTabName.AVAILABLE_ESIM ||
    activeTab === SprSimListTabName.AVAILABLE_PSIM;

  if (!canDeleteSim && !canActivateSim) return <td>&nbsp;</td>;

  return (
    <td>
      {canActivateSim && activateButton}
      {canDeleteSim && deleteButton}
    </td>
  );
}
